// Prompts: the game's toasts and dialogs ({ prompt_type: "prompt_toast" | "prompt_general", title, text }), plus the app's own.
import { create } from "zustand";
import type { Prompt } from "@/api/types";
import { GamePromptError, onPrompts } from "@/api/http";

export interface Toast {
  id: number;
  text: string;
  kind: "info" | "error" | "success";
}

export interface DialogPrompt {
  id: number;
  title: string;
  text: string;
}

interface PromptState {
  toasts: Toast[];
  dialog: DialogPrompt | null;
  toast: (text: string, kind?: Toast["kind"]) => void;
  dialogShow: (title: string, text: string) => void;
  dialogClose: () => void;
  dismiss: (id: number) => void;
  show: (prompts: Prompt[], kind?: Toast["kind"]) => void;
  error: (e: unknown) => void;
}

let seq = 1;

/** The world process answers failures with bare reason codes (often JSON-quoted) — say them in plain words. */
const REASONS: Record<string, string> = {
  INSUFFICIENT_RESOURCES: "Not enough resources in this city.",
  INSUFFICIENT_POPULATION: "Not enough free population — upgrade the Farm.",
  INSUFFICIENT_MERCHANTS: "Not enough free merchants.",
  INSUFFICIENT_TROOPS: "Not enough troops at home.",
  INSUFFICIENT_INVENTORY: "You don't have that item.",
  INSUFFICIENT_OFFERS: "That offer is gone.",
  INSUFFICIENT_SCHOLAR_CAPACITY: "You can't educate more scholars yet.",
  OUTSIDE_LEVEL_BOUNDS: "That level isn't available.",
  INVALID_ASSIGNMENT_ID: "That item is no longer in the queue.",
  INVALID_PARAMETER: "The game refused that request.",
  MISSING_BUILDING_ACADEMY: "This city has no Academy.",
  MISSING_OWNER: "This city has no owner.",
  VILLAGE_NOT_FOUND: "There is no city at those coordinates.",
  ARMY_NOT_FOUND: "That army is no longer out.",
};
export function friendlyText(text: string): string {
  const t = text.trim().replace(/^"+|"+$/g, "");
  return REASONS[t] ?? t;
}

export const usePrompts = create<PromptState>((set, get) => ({
  toasts: [],
  dialog: null,
  toast: (text, kind = "info") => {
    const id = seq++;
    set((s) => ({ toasts: [...s.toasts.slice(-3), { id, text, kind }] }));
    window.setTimeout(() => get().dismiss(id), kind === "error" ? 6000 : 3500);
  },
  dialogShow: (title, text) => set({ dialog: { id: seq++, title, text } }),
  dialogClose: () => set({ dialog: null }),
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  show: (prompts, kind = "info") => {
    for (const p of prompts) {
      if (!p || !p.text) continue;
      const text = friendlyText(p.text);
      if (p.prompt_type === "prompt_general") get().dialogShow(p.title || "Valor", text);
      else get().toast(text, kind);
    }
  },
  error: (e) => {
    if (e instanceof GamePromptError) {
      if (e.code === "signed_out") return;
      get().show(e.prompts, "error");
    } else if (e instanceof Error) {
      get().toast(e.message || "Something went wrong.", "error");
    } else {
      get().toast("Something went wrong.", "error");
    }
  },
}));

// flash messages on successful game answers
onPrompts((prompts) => usePrompts.getState().show(prompts, "info"));
